if (typeof ProductModelShowcase === 'undefined') {
  class ProductModelShowcase extends HTMLElement {
    connectedCallback() {
      this.currentIndex = parseInt(this.dataset.initialIndex, 10) || 0;
      this._drag = {
        active: false, context: null,
        startX: 0, startY: 0, lastX: 0,
        lastTime: 0, velocity: 0, offset: 0,
        baseOffset: 0, direction: null
      };
      this.tabs = [...this.querySelectorAll('.model-showcase__tab')];
      this.slides = [...this.querySelectorAll('.model-showcase__slide')];
      this.infos = [...this.querySelectorAll('.model-showcase__info')];
      this.pills = [...this.querySelectorAll('.model-showcase__pill-row__pill')];
      this.track = this.querySelector('[data-track]');
      this.mobileTrack = this.querySelector('[data-mobile-track]');
      this.dots = [...this.querySelectorAll('.model-showcase__dot')];
      this.infoTrack = this.querySelector('[data-info-track]');
      this._bindEvents();
      this._updateDesktopTrack();
      this._updateMobileTrack();
    }

    _bindEvents() {
      this.tabs.forEach(t => t.addEventListener('click', () => this._goTo(+t.dataset.index)));
      this.dots.forEach(t => t.addEventListener('click', () => this._goTo(+t.dataset.index)));
      this.slides.forEach(s => {
        s.addEventListener('click', () => {
          if (this._didDrag) return;
          const idx = +s.dataset.index;
          if (idx !== this.currentIndex) this._goTo(idx);
        });
      });

      this.addEventListener('click', e => {
        const sw = e.target.closest('.model-showcase__swatch');
        if (!sw) return;
        e.stopPropagation();
        const info = sw.closest('.model-showcase__info');
        const mSlide = sw.closest('.model-showcase__mobile-slide');
        const sIdx = info ? +info.dataset.index : +(mSlide?.dataset.index ?? 0);
        this._activateSwatch(sIdx, +sw.dataset.swatchIndex);
      });

      // Desktop carousel — pointer events with capture for reliable drag on mouse + trackpad
      const wrap = this.querySelector('.model-showcase__carousel-wrap');
      if (wrap) {
        wrap.addEventListener('pointerdown', e => {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          try { wrap.setPointerCapture(e.pointerId); } catch(_) {}
          this._pointerStart(e.clientX, e.clientY, 'desktop');
          wrap.style.cursor = 'grabbing';
        });
        wrap.addEventListener('pointermove', e => {
          if (this._drag.context !== 'desktop') return;
          this._pointerMove(e.clientX, e.clientY, e);
        });
        wrap.addEventListener('pointerup', e => {
          if (this._drag.context !== 'desktop') return;
          wrap.style.cursor = '';
          this._pointerEnd(e.clientX);
        });
        wrap.addEventListener('pointercancel', () => { wrap.style.cursor = ''; this._cancelDrag(); });
        wrap.addEventListener('dragstart', e => e.preventDefault());
        wrap.addEventListener('wheel', e => this._onWheel(e, 'desktop'), { passive: true });
      }

      // Mobile track — native CSS scroll-snap; JS only syncs state on settle
      if (this.mobileTrack) {
        const syncFn = () => { if (!this._programmaticScroll) this._syncFromScroll(); };
        if ('onscrollend' in window) {
          this.mobileTrack.addEventListener('scrollend', syncFn, { passive: true });
        } else {
          this.mobileTrack.addEventListener('scroll', () => {
            clearTimeout(this._scrollTimer);
            this._scrollTimer = setTimeout(syncFn, 150);
          }, { passive: true });
        }
      }

      window.addEventListener('resize', () => {
        this._cancelDrag();
        this._updateDesktopTrack();
        this._updateMobileTrack();
      });
    }

    _pointerStart(x, y, context) {
      const d = this._drag;
      d.active = false;
      d.context = context;
      d.startX = x; d.startY = y; d.lastX = x;
      d.lastTime = Date.now();
      d.velocity = 0; d.offset = 0; d.direction = null;
      if (context === 'mobile' && this.mobileTrack) {
        d.baseOffset = -this.currentIndex * this.mobileTrack.parentElement.offsetWidth;
      }
    }

    _pointerMove(x, y, event) {
      const d = this._drag;
      if (!d.context) return;
      const dx = x - d.startX, dy = y - d.startY;

      if (!d.direction) {
        if (Math.sqrt(dx * dx + dy * dy) < 4) return;
        d.direction = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
      }
      if (d.direction === 'v') return;

      if (!d.active) {
        d.active = true;
        if (d.context === 'desktop') {
          this.slides.forEach(s => { s.style.transition = 'none'; });
          if (this.infoTrack) this.infoTrack.style.transition = 'none';
        }
        if (d.context === 'mobile' && this.mobileTrack) this.mobileTrack.style.transition = 'none';
      }

      if (event) event.preventDefault();

      const now = Date.now(), dt = now - d.lastTime;
      if (dt > 0) d.velocity = (x - d.lastX) / dt;
      d.lastX = x; d.lastTime = now;
      d.offset = dx;

      if (d.context === 'desktop') this._calcSlidePositions(dx);
      if (d.context === 'mobile' && this.mobileTrack) {
        this.mobileTrack.style.transform = `translateX(${d.baseOffset + dx}px)`;
      }
    }

    _pointerEnd(x) {
      const d = this._drag;
      if (!d.context) return;
      const ctx = d.context;
      d.context = null;

      if (!d.active) { this._didDrag = false; return; }

      this._didDrag = true;
      setTimeout(() => { this._didDrag = false; }, 100);

      const diff = d.startX - x;
      const speed = Math.abs(d.velocity);
      const shouldNav = Math.abs(diff) > 30 || speed > 0.2;
      const n = this.slides.length;
      let next = this.currentIndex;

      if (shouldNav) {
        next = diff > 0
          ? Math.min(this.currentIndex + 1, n - 1)
          : Math.max(this.currentIndex - 1, 0);
      }

      if (ctx === 'desktop') {
        this.slides.forEach(s => { s.style.transition = ''; });
        if (this.infoTrack) this.infoTrack.style.transition = '';
      }
      if (ctx === 'mobile' && this.mobileTrack) this.mobileTrack.style.transition = '';

      next !== this.currentIndex ? this._goTo(next) : (this._updateDesktopTrack(), this._updateMobileTrack());
    }

    _cancelDrag() {
      const d = this._drag;
      if (!d.context) return;
      d.context = null; d.active = false;
      this.slides.forEach(s => { s.style.transition = ''; });
      if (this.infoTrack) this.infoTrack.style.transition = '';
      if (this.mobileTrack) this.mobileTrack.style.transition = '';
      this._updateDesktopTrack();
      this._updateMobileTrack();
    }

    _onWheel(event, context) {
      const ax = Math.abs(event.deltaX), ay = Math.abs(event.deltaY);
      // Require meaningful horizontal movement and not primarily vertical scroll
      if (ax < 3 || ay > ax * 2.5) return;
      const now = Date.now();
      if (this._lastWheel && now - this._lastWheel < 200) return;
      this._lastWheel = now;
      const n = (context === 'mobile' ? this.querySelectorAll('.model-showcase__mobile-slide').length : this.slides.length);
      const next = event.deltaX > 0
        ? Math.min(this.currentIndex + 1, n - 1)
        : Math.max(this.currentIndex - 1, 0);
      this._goTo(next);
    }

    _activateSwatch(slideIdx, swatchIdx) {
      this.querySelectorAll(
        `.model-showcase__info[data-index="${slideIdx}"] .model-showcase__swatch,
         .model-showcase__mobile-slide[data-index="${slideIdx}"] .model-showcase__swatch`
      ).forEach(s => s.classList.toggle('is-active', +s.dataset.swatchIndex === swatchIdx));

      [this.slides[slideIdx], this.querySelector(`.model-showcase__mobile-slide[data-index="${slideIdx}"]`)]
        .forEach(c => {
          if (!c) return;
          const imgs = [...c.querySelectorAll('[data-swatch-img]')];
          imgs.forEach(i => i.classList.remove('is-active'));
          (c.querySelector(`[data-swatch-img="${swatchIdx}"]`) || imgs[0])?.classList.add('is-active');
        });
    }

    _goTo(index) {
      this.currentIndex = index;
      this._update();
    }

    _update() {
      const i = this.currentIndex;
      this.tabs.forEach((t, idx) => {
        t.classList.toggle('is-active', idx === i);
        t.setAttribute('aria-selected', idx === i ? 'true' : 'false');
      });
      this.slides.forEach((s, idx) => s.classList.toggle('is-active', idx === i));
      this.querySelectorAll('.model-showcase__mobile-slide').forEach((s, idx) => s.classList.toggle('is-active', idx === i));
      this.infos.forEach((info, idx) => info.classList.toggle('is-active', idx === i));
      this.dots.forEach((dot, idx) => dot.classList.toggle('is-active', idx === i));
      this.pills.forEach((p, idx) => p.classList.toggle('is-active', idx === i));
      this._updateDesktopTrack();
      this._updateMobileTrack();
    }

    _calcSlidePositions(extraOffset) {
      if (!this.slides.length || !this.track) return;
      const wrapWidth = this.track.parentElement.offsetWidth;
      const slideWidth = this.slides[0].offsetWidth;
      const center = wrapWidth / 2 - slideWidth / 2;
      // Adjacent slides peek 25% of viewport width from each edge — no infinite wrap
      const peek = wrapWidth * 0.25;
      const step = wrapWidth - peek - center;
      const off = extraOffset || 0;

      this.slides.forEach((slide, i) => {
        const dist = i - this.currentIndex;
        const x = center + dist * step + off;
        const isActive = dist === 0;
        const scale = isActive ? 1 : 0.88;
        slide.style.transform = `translateX(${x}px) translateY(-50%) scale(${scale})`;
        slide.style.opacity = isActive ? '1' : '0.45';
        slide.style.zIndex = isActive ? '2' : '1';
      });

      if (this.infoTrack && step > 0) {
        const infoW = this.infoTrack.parentElement.offsetWidth;
        const infoOff = off !== 0 ? off * (infoW / step) : 0;
        this.infoTrack.style.transform = `translateX(${-this.currentIndex * infoW + infoOff}px)`;
      }
    }

    _updateDesktopTrack() {
      this._calcSlidePositions(0);
      this.classList.add('is-initialized');
    }

    _updateMobileTrack() {
      if (!this.mobileTrack) return;
      const slides = [...this.mobileTrack.querySelectorAll('.model-showcase__mobile-slide')];
      slides.forEach((s, idx) => s.classList.toggle('is-active', idx === this.currentIndex));
      const target = slides[this.currentIndex];
      if (!target) return;
      this._programmaticScroll = true;
      const left = Math.max(0, target.offsetLeft + target.offsetWidth / 2 - this.mobileTrack.offsetWidth / 2);
      this.mobileTrack.scrollTo({ left, behavior: 'smooth' });
      clearTimeout(this._progTimer);
      this._progTimer = setTimeout(() => { this._programmaticScroll = false; }, 700);
    }

    _syncFromScroll() {
      if (!this.mobileTrack) return;
      const slides = [...this.mobileTrack.querySelectorAll('.model-showcase__mobile-slide')];
      if (!slides.length) return;
      const center = this.mobileTrack.scrollLeft + this.mobileTrack.offsetWidth / 2;
      let best = 0, minDist = Infinity;
      slides.forEach((s, i) => {
        const d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - center);
        if (d < minDist) { minDist = d; best = i; }
      });
      if (best === this.currentIndex) return;
      this.currentIndex = best;
      const i = best;
      this.tabs.forEach((t, idx) => { t.classList.toggle('is-active', idx === i); t.setAttribute('aria-selected', idx === i ? 'true' : 'false'); });
      this.slides.forEach((s, idx) => s.classList.toggle('is-active', idx === i));
      slides.forEach((s, idx) => s.classList.toggle('is-active', idx === i));
      this.infos.forEach((info, idx) => info.classList.toggle('is-active', idx === i));
      this.dots.forEach((dot, idx) => dot.classList.toggle('is-active', idx === i));
      this.pills.forEach((p, idx) => p.classList.toggle('is-active', idx === i));
    }
  }

  customElements.define('product-model-showcase', ProductModelShowcase);

  document.addEventListener('shopify:section:load', e => {
    const el = e.target.querySelector('product-model-showcase');
    if (el) el.connectedCallback();
  });
}
